import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PivotGrid, { Export, FieldChooser } from 'devextreme-react/pivot-grid';

import { Workbook } from 'exceljs';
import moment from 'moment';

import { AppLayoutTop } from 'components/appComponents';
import { Button, DateRangeBox } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import { exportPivotGrid } from 'devextreme/excel_exporter';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { saveAs } from 'file-saver-es';
import $createStore from 'utils/create-store-instance';

import { AutoFill, CreateEditModal } from './components';

// Our demo infrastructure requires us

const EmployeesReport: FC = () => {
  const refGrid = useRef<PivotGrid | null>(null);

  const [dateBegin, setDateBegin] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [dateEnd, setDateEnd] = useState(moment().startOf('month').format('YYYY-MM-DD'));

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalIsVisibleAutoFill, setModalIsVisibleAutoFill] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [currentDate, setCurrentDate] = useState<string>('');

  const dataSource = useMemo(() => {
    return $createStore(`/v1/Shift/report?dateBegin=${dateBegin}&dateEnd=${dateEnd}`, 'id');
  }, [dateBegin, dateEnd]);

  const dataSourcePivot = useMemo(
    () =>
      new PivotGridDataSource({
        fields: [
          {
            caption: 'EmployeeName',
            dataField: 'employeeId',
            area: 'row',
            selector(data) {
              return data.name;
            },
          },
          {
            dataField: 'date',
            dataType: 'date',
            area: 'column',
            groupName: 'Date',
          },
          { groupName: 'Date', groupInterval: 'year', groupIndex: 0 },
          { groupName: 'Date', groupInterval: 'month', groupIndex: 1 },
          {
            caption: 'sum',
            dataField: 'amount',
            dataType: 'number',
            summaryType: 'sum',
            area: 'data',
            selector() {
              return 1;
            },
          },
        ],
        store: dataSource,
      }),
    [dataSource],
  );

  const refresh = () => refGrid.current?.instance.getDataSource().reload();

  useEffect(() => {
    setTimeout(() => {
      var ds = refGrid.current?.instance.getDataSource();

      if (ds) {
        var state = ds.state();
        state.columnExpandedPaths.push([new Date().getFullYear()]);
        ds.state(state);
      }
    }, 200);
  }, [dateBegin, dateEnd]);

  const onCellClick = async (e) => {
    var dataSource,
      pivotGridDataSource = e.component.getDataSource(),
      cellObject;
    if (e.area === 'data') {
      cellObject = e.cell;
    } else if (e.area === 'row') {
      cellObject = {
        rowPath: e.cell.path,
        dataIndex: 0,
      };
    } else if (e.area === 'column') {
      cellObject = {
        columnPath: e.cell.path,
        dataIndex: 0,
      };
    }

    if (cellObject) {
      dataSource = new DataSource({
        store: pivotGridDataSource.createDrillDownDataSource(cellObject).store(),
        paginate: false,
        key: 'employeeId',
      });
      dataSource.load().done(function (result) {
        const employee = result && result.length && result[0];
        const currentPath = e.cell.columnPath && e.cell.columnPath.length > 1;

        if (employee && currentPath) {
          setCurrentDate(employee.date);
          setDetailId(employee.employeeId);
          setModalIsVisible(true);
        }
      });
    }
  };

  const onExporting = useCallback((e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sales');

    exportPivotGrid({
      component: e.component,
      worksheet,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'report_employee.xlsx');
      });
    });
    e.cancel = true;
  }, []);

  const handleChangeDate = (dateRange: Array<string | Date | number>) => {
    dateRange[0] && setDateBegin(dateRange[0].toString());
    dateRange[1] && setDateEnd(dateRange[1].toString());
  };

  return (
    <div>
      <AppLayoutTop title="Отчет по сменам">
        {modalIsVisible && (
          <CreateEditModal
            changeVisible={setModalIsVisible}
            id={detailId}
            refresh={refresh}
            visible={modalIsVisible}
            currentDate={currentDate}
          />
        )}

        {modalIsVisibleAutoFill && (
          <AutoFill visible={modalIsVisibleAutoFill} changeVisible={setModalIsVisibleAutoFill} refresh={refresh} />
        )}

        <div className="d-flex align-center">
          <DateRangeBox
            value={[dateBegin, dateEnd]}
            onValueChanged={(e) => handleChangeDate(e.value)}
            dateSerializationFormat="yyyy-MM-dd"
            stylingMode="underlined"
            useMaskBehavior
            className="mr-2"
          />
          <Button
            width={120}
            text="Добавить"
            type="default"
            onClick={() => {
              setDetailId(null);
              setModalIsVisible(true);
            }}
          />
          <Button
            text="Автозаполнение"
            className="ml-2"
            type="default"
            onClick={() => setModalIsVisibleAutoFill(true)}
          />
        </div>
      </AppLayoutTop>

      <PivotGrid
        ref={refGrid}
        id="pivotgrid"
        key="employeeId"
        dataSource={dataSourcePivot}
        allowSortingBySummary={true}
        allowFiltering={true}
        showBorders={true}
        showColumnTotals={false}
        onCellClick={onCellClick}
        showColumnGrandTotals={true}
        showRowTotals={true}
        showRowGrandTotals={false}
        onExporting={onExporting}
      >
        <Export enabled={true} />
        <FieldChooser enabled={true} height={400} />
      </PivotGrid>
    </div>
  );
};

export default EmployeesReport;
